import { FIELD_TYPES, type FieldType, type PicklistOption } from './fields.js';

/**
 * One condition document, for every rule in the product.
 *
 * A pricelist rule yields a price. A sharing rule yields visibility. A segment
 * yields a count of people. An automation trigger yields whether to run. Those
 * outcomes have nothing in common, and it is tempting to conclude the *rules*
 * have nothing in common either - which is how six rule builders get written,
 * five of them missing nested groups and three of them disagreeing about what
 * "is empty" means for a number.
 *
 * What they share is the shape of the question: fields, operators, values,
 * grouped by all/any/none, nested. That is this file. What differs is which
 * fields may be asked about and what the answer is used for, and that is a
 * `RuleSchema` the caller supplies.
 *
 * So: one document, one validator, one evaluator, six schemas. When a caller
 * genuinely needs something different it takes a schema, and it does not fork.
 */

export const OPERATORS = [
  'eq',
  'ne',
  'lt',
  'lte',
  'gt',
  'gte',
  'between',
  'contains',
  'starts-with',
  'in',
  'not-in',
  'is-set',
  'is-not-set',
] as const;

export type Operator = (typeof OPERATORS)[number];

/** Operators that take no value at all - the ones people forget to handle. */
export const VALUELESS_OPERATORS: readonly Operator[] = ['is-set', 'is-not-set'];
/** Operators taking a list rather than a single value. */
export const LIST_OPERATORS: readonly Operator[] = ['in', 'not-in'];
/** Operators taking exactly two values. */
export const RANGE_OPERATORS: readonly Operator[] = ['between'];

export interface Condition {
  field: string;
  operator: Operator;
  /** Present for single-value operators. */
  value?: string | number | boolean | null;
  /** Present for `in`, `not-in` and `between`. */
  values?: Array<string | number | boolean | null>;
}

export interface AllGroup {
  all: RuleNode[];
}
export interface AnyGroup {
  any: RuleNode[];
}
export interface NoneGroup {
  none: RuleNode[];
}

export type RuleGroup = AllGroup | AnyGroup | NoneGroup;
export type RuleNode = Condition | RuleGroup;

export function isGroup(node: RuleNode): node is RuleGroup {
  return 'all' in node || 'any' in node || 'none' in node;
}

export function groupChildren(group: RuleGroup): RuleNode[] {
  if ('all' in group) return group.all;
  if ('any' in group) return group.any;
  return group.none;
}

export function groupKind(group: RuleGroup): 'all' | 'any' | 'none' {
  if ('all' in group) return 'all';
  if ('any' in group) return 'any';
  return 'none';
}

/** A field a rule of this kind may ask about. */
export interface RuleField {
  key: string;
  label: string;
  type: FieldType;
  /** Restricts the operators offered. Absent means the type's defaults. */
  operators?: readonly Operator[];
  options?: readonly PicklistOption[];
  /** One line explaining what this field means, for the builder. */
  help?: string;
}

/**
 * What one caller's rules are about.
 *
 * `maxDepth` is the compiler's depth, not a design preference: a builder that
 * lets somebody nest five levels deep and then refuses to save is a builder
 * that wasted their afternoon.
 */
export interface RuleSchema {
  key: string;
  label: string;
  fields: readonly RuleField[];
  maxDepth: number;
  /** What a matching rule produces, named so the builder can say so. */
  outcome: string;
  /**
   * What a rule with no conditions at the top level means here.
   *
   * The two callers genuinely differ and the difference is not cosmetic.
   *
   * A **segment** with no conditions is a question nobody finished asking, and
   * reading it as "everybody" turns a half-built audience into a send to the
   * whole database. It means 'nothing'.
   *
   * A **report filter** with no conditions is a complete statement: all deals.
   * It is what the builder opens on, and refusing it would mean a report
   * cannot be unfiltered. It means 'everything'.
   *
   * Required rather than defaulted, because a default here is a decision taken
   * by whoever wrote this line on behalf of callers who have not thought about
   * it - and one of the two answers ends in a regulator's letter. A nested
   * empty group is unfinished either way and is always a problem.
   */
  emptyMeans: 'nothing' | 'everything';
}

/** Which operators make sense for a type, when a field does not say. */
export function operatorsFor(type: FieldType): readonly Operator[] {
  switch (type) {
    case 'number':
    case 'decimal':
    case 'currency':
    case 'percent':
    case 'date':
    case 'datetime':
    case 'formula':
    case 'rollup':
      return ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'between', 'is-set', 'is-not-set'];
    case 'boolean':
      return ['eq', 'is-set', 'is-not-set'];
    case 'picklist':
    case 'multipicklist':
    case 'tags':
    case 'lookup':
    case 'user':
      return ['eq', 'ne', 'in', 'not-in', 'is-set', 'is-not-set'];
    case 'text':
    case 'textarea':
    case 'email':
    case 'phone':
    case 'url':
    case 'address':
    case 'file':
      return ['eq', 'ne', 'contains', 'starts-with', 'in', 'not-in', 'is-set', 'is-not-set'];
  }
}

export interface RuleProblem {
  /** A path like `all.0.any.2`, so a builder can point at the row. */
  path: string;
  message: string;
}

/**
 * Checks a document against a schema.
 *
 * Used by the builder as somebody types AND by the server before it saves.
 * One function, so a rule the builder accepted cannot be refused on save -
 * which is the single most common way a rule builder becomes unusable.
 */
export function validateRule(node: RuleNode, schema: RuleSchema): RuleProblem[] {
  const problems: RuleProblem[] = [];
  walk(node, schema, '', 1, problems);
  return problems;
}

function walk(
  node: RuleNode,
  schema: RuleSchema,
  path: string,
  depth: number,
  problems: RuleProblem[],
): void {
  if (isGroup(node)) {
    const kind = groupKind(node);
    const children = groupChildren(node);
    if (depth > schema.maxDepth) {
      problems.push({
        path,
        message: `Rules can nest ${String(schema.maxDepth)} levels deep. This is ${String(depth)}.`,
      });
      return;
    }
    if (children.length === 0) {
      /*
       * An empty group nested inside another is unfinished, always: somebody
       * added a group and did not fill it in, and either reading would be
       * their rule quietly doing the opposite of what they meant.
       *
       * An empty group at the TOP is the schema's decision. "All deals" is a
       * complete thought; "all contacts, as an audience" is usually not.
       */
      const isRoot = depth === 1;
      if (!isRoot || schema.emptyMeans === 'nothing') {
        problems.push({ path, message: 'This group has no conditions in it.' });
      }
    }
    children.forEach((child, index) => {
      walk(child, schema, path === '' ? `${kind}.${String(index)}` : `${path}.${kind}.${String(index)}`, depth + 1, problems);
    });
    return;
  }

  const field = schema.fields.find((entry) => entry.key === node.field);
  if (!field) {
    problems.push({
      path,
      message: `${schema.label} rules cannot ask about "${node.field}".`,
    });
    return;
  }

  const allowed = field.operators ?? operatorsFor(field.type);
  if (!allowed.includes(node.operator)) {
    problems.push({
      path,
      message: `${field.label} cannot be compared with "${node.operator}".`,
    });
    return;
  }

  if (VALUELESS_OPERATORS.includes(node.operator)) {
    if (node.value !== undefined || node.values !== undefined) {
      problems.push({ path, message: `"${node.operator}" takes no value.` });
    }
    return;
  }

  if (LIST_OPERATORS.includes(node.operator)) {
    if (!node.values || node.values.length === 0) {
      problems.push({ path, message: `"${node.operator}" needs at least one value.` });
    }
    return;
  }

  if (RANGE_OPERATORS.includes(node.operator)) {
    if (!node.values || node.values.length !== 2) {
      problems.push({ path, message: '"between" needs exactly two values.' });
    }
    return;
  }

  if (node.value === undefined || node.value === null || node.value === '') {
    // A condition with no value is the shape of a rule that matches nothing
    // and reads as a rule that matches everything.
    problems.push({ path, message: `${field.label} needs a value to compare against.` });
  }
}

/** How many conditions a document holds, at any depth. */
export function countConditions(node: RuleNode): number {
  if (!isGroup(node)) return 1;
  return groupChildren(node).reduce((total, child) => total + countConditions(child), 0);
}

export function ruleDepth(node: RuleNode): number {
  if (!isGroup(node)) return 0;
  const children = groupChildren(node);
  if (children.length === 0) return 1;
  return 1 + Math.max(...children.map(ruleDepth));
}

/** The field types a rule schema may use. Derived, so nothing drifts. */
export const RULE_FIELD_TYPES: readonly FieldType[] = FIELD_TYPES;
